// Typed client for patient self-registration.
//
// Split from patientsClient because half of it is unauthenticated: the two
// public calls run on a page with no session, and pass `auth: false` so a 4xx
// cannot trip the shared 401 handler into redirecting a patient to the staff
// sign-in screen.

import { api, ApiError, clinicApi } from '@/lib/api';
import type {
  ApprovalResult,
  ApproveSelfRegistrationInput,
  PublicClinicInfo,
  SelfRegistration,
  SelfRegistrationDetail,
  SelfRegistrationDetailDTO,
  SelfRegistrationDTO,
  SelfRegistrationList,
  SelfRegistrationListDTO,
  SelfRegistrationStatus,
  SubmitSelfRegistrationInput,
} from '@/types/patients';

function toDate(iso: string): Date {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    throw new ApiError('PARSE_ERROR', `Invalid timestamp from server: ${iso}`, 0);
  }
  return d;
}

export function parseSelfRegistration(dto: SelfRegistrationDTO): SelfRegistration {
  return {
    ...dto,
    submittedAt: toDate(dto.submittedAt),
    reviewedAt: dto.reviewedAt === null ? null : toDate(dto.reviewedAt),
  };
}

function parseDetail(dto: SelfRegistrationDetailDTO): SelfRegistrationDetail {
  return { ...parseSelfRegistration(dto), nationalId: dto.nationalId, nationalIdUnreadable: dto.nationalIdUnreadable };
}

export const selfRegistrationApi = {
  // ---------- Public (no session) ----------

  getClinic: (clinicId: string) =>
    api.get<PublicClinicInfo>(
      `/api/public/self-registration/${encodeURIComponent(clinicId)}`,
      { auth: false },
    ),

  submit: (clinicId: string, input: SubmitSelfRegistrationInput) =>
    api.post<{ submitted: true }>(
      `/api/public/self-registration/${encodeURIComponent(clinicId)}`,
      input,
      { auth: false },
    ),

  // ---------- Staff review queue ----------

  list: async (
    status: SelfRegistrationStatus = 'pending',
    page: { limit?: number; offset?: number } = {},
  ): Promise<SelfRegistrationList> => {
    const qs = new URLSearchParams({ status });
    if (page.limit !== undefined) qs.set('limit', String(page.limit));
    if (page.offset !== undefined) qs.set('offset', String(page.offset));
    const dto = await clinicApi.get<SelfRegistrationListDTO>(`self-registrations?${qs}`);
    return { items: dto.items.map(parseSelfRegistration), count: dto.count };
  },

  get: async (id: string): Promise<SelfRegistrationDetail> =>
    parseDetail(await clinicApi.get<SelfRegistrationDetailDTO>(`self-registrations/${id}`)),

  approve: (id: string, input: ApproveSelfRegistrationInput) =>
    clinicApi.post<ApprovalResult>(`self-registrations/${id}/approve`, input),

  reject: (id: string) =>
    clinicApi.post<{ submissionId: string }>(`self-registrations/${id}/reject`),
};
